import type { Range } from "@tiptap/core";
import type { Attrs, MarkType, Node } from "@tiptap/pm/model";
import type { Transaction } from "@tiptap/pm/state";

const canCarryComment = (node: Node) => Boolean(node?.type?.spec?.attrs?.comment);

/**
 * A code block allows no marks, so it takes the comment on the node whatever part of it was selected;
 * any other node takes it only when the range holds the node whole.
 */
const takesCommentOnNode = (node: Node, pos: number, range: Range, markType: MarkType) => {
	if (!canCarryComment(node)) return false;
	if (node.isTextblock) return !node.type.allowsMarkType(markType);
	return range.from <= pos && range.to >= pos + node.nodeSize;
};

interface BlockMarkInterface {
	setMarkup(range: Range, attributes: Attrs): Transaction;
	deleteMarkup(range: Range): Transaction;
}

export interface BlockMarkData {
	data: Attrs;
	position: number;
	isFromBlock: boolean;
	range: Range;
}

class CommentBlockMark implements BlockMarkInterface {
	constructor(
		private readonly _tr: Transaction,
		private readonly _markType: MarkType,
	) {}

	setMarkup(range: Range, attributes: Attrs) {
		return this._set(range, attributes);
	}

	deleteMarkup(range: Range | Range[]) {
		if (Array.isArray(range)) {
			range.forEach((r) => r && this._delete(r));
			return this._tr;
		}

		return this._delete(range);
	}

	private _delete(range: Range) {
		this._tr.doc.nodesBetween(range.from, range.to, (node, pos) => {
			if (node.isText) {
				this._deleteTextAttributes(this._tr, {
					from: Math.max(pos, range.from),
					to: Math.min(pos + node.nodeSize, range.to),
				});
				return;
			}

			if (!takesCommentOnNode(node, pos, range, this._markType)) return;
			this._deleteBlockAttributes(this._tr, pos);
			return false;
		});

		return this._tr;
	}

	private _set(range: Range, attributes: Attrs) {
		this._tr.doc.nodesBetween(range.from, range.to, (node, pos) => {
			if (node.isText) {
				this._setTextAttributes(
					this._tr,
					{ from: Math.max(pos, range.from), to: Math.min(pos + node.nodeSize, range.to) },
					attributes,
				);
				return;
			}

			if (!takesCommentOnNode(node, pos, range, this._markType)) return;
			this._setBlockAttributes(this._tr, pos, attributes);
			return false;
		});

		return this._tr;
	}

	private _setBlockAttributes(tr: Transaction, pos: number, attributes: Attrs) {
		tr.setNodeAttribute(pos, this._markType.name, attributes);
	}

	private _deleteBlockAttributes(tr: Transaction, pos: number) {
		tr.setNodeAttribute(pos, this._markType.name, undefined);
	}

	private _setTextAttributes(tr: Transaction, range: Range, attributes: Attrs) {
		tr.addMark(range.from, range.to, this._markType.create(attributes));
	}

	private _deleteTextAttributes(tr: Transaction, range: Range) {
		tr.removeMark(range.from, range.to, this._markType);
	}
}

export default CommentBlockMark;
